"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { showToast } from "@/utils/toast";

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [permission, setPermission] = useState("default");
  const [notifications, setNotifications] = useState([]);
  const [isClient, setIsClient] = useState(false);

  // Check if we're on the client side
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if (!isClient) return;

    if (typeof window !== "undefined" && "Notification" in window) {
      setPermission(Notification.permission);

      if (Notification.permission === "default") {
        Notification.requestPermission().then((result) => {
          setPermission(result);
        });
      }
    }
  }, [isClient]);

  // Show web notification
  const showWebNotification = (message, type = "info", duration = 4000) => {
    if (typeof window !== "undefined") {
      // Try direct function call first
      if (window.showWebNotification) {
        window.showWebNotification(message, type, duration);
      } else {
        // Wait a bit and try again (component might not be mounted yet)
        setTimeout(() => {
          if (window.showWebNotification) {
            window.showWebNotification(message, type, duration);
          } else {
            // Fallback to event
            const event = new CustomEvent("webNotification", {
              detail: { message, type, duration },
            });
            window.dispatchEvent(event);
          }
        }, 100);
      }
    }
  };

  // Show toast notification
  const showToastNotification = (message, type = "info", duration = 4000) => {
    if (typeof window !== "undefined") {
      showToast(message, type, duration);
    }
  };

  // Show browser push notification
  const showBrowserNotification = (title, options = {}) => {
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      permission === "granted"
    ) {
      const notification = new Notification(title, {
        icon: "/images/icon/brand.svg",
        badge: "/images/icon/brand.svg",
        ...options,
      });

      setTimeout(() => notification.close(), 5000);
      return notification;
    }
    return null;
  };

  const notify = (message, type = "info", options = {}) => {
    const {
      showToast: enableToast = false,
      showBrowser: enableBrowser = false, // Disabled to avoid duplicates
      showWeb: enableWeb = true,
      duration = 5000,
      ...browserOptions
    } = options;

    if (enableWeb) {
      showWebNotification(message, type, duration);
    }

    if (enableToast) {
      showToastNotification(message, type, duration);
    }

    if (enableBrowser && permission === "granted") {
      showBrowserNotification(message, {
        body: browserOptions.body || message,
        tag: browserOptions.tag || "hospital-notification",
        ...browserOptions,
      });
    }
  };

  const success = (message, options = {}) =>
    notify(message, "success", {
      showWeb: true,
      showBrowser: false,
      ...options,
    });
  const error = (message, options = {}) =>
    notify(message, "error", { showWeb: true, showBrowser: false, ...options });
  const warning = (message, options = {}) =>
    notify(message, "warning", {
      showWeb: true,
      showBrowser: false,
      ...options,
    });
  const info = (message, options = {}) =>
    notify(message, "info", { showWeb: true, showBrowser: false, ...options });

  const requestPermission = async () => {
    if (typeof window !== "undefined" && "Notification" in window) {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    }
    return "denied";
  };

  const value = {
    permission,
    notifications,
    notify,
    success,
    error,
    warning,
    info,
    showToast: showToastNotification,
    showWeb: showWebNotification,
    showBrowser: showBrowserNotification,
    requestPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
